import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { inr, fmtDate, statusBadgeClass, waLink } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import {
  ClipboardList,
  ScissorsLineDashed,
  IndianRupee,
  CalendarClock,
  ArrowUpRight,
  TrendingUp,
  AlertTriangle,
  MessageCircle,
  Video,
  MapPin,
  Camera,
} from "lucide-react";
import { Link } from "react-router-dom";
import MonthlyFinancials from "@/components/MonthlyFinancials";
import ProfitLossOverview from "@/components/ProfitLossOverview";
import PageHero from "@/components/PageHero";
import MetricCard from "@/components/MetricCard";
import CommandCenter from "@/components/CommandCenter";

/**
 * TodayOnSet — one-glance snapshot of every crew assignment for today, pulled
 * from /api/team/crew/today and grouped by client. Uses a compact
 * "role → staff → gear" row so the admin can eyeball "who is where with what"
 * without opening the Shoot Crew Management tab.
 *
 * If today has no assignments we render a friendly empty state that links
 * straight into the crew tab so it's one click to schedule.
 */
function TodayOnSet({ data }) {
  const dateLabel = data?.date ? fmtDate(data.date) : "…";
  const groups = data?.groups || [];
  // iter97 — Header now leads with the SHOOT count (primary metric) and
  // shows crew-member count as the secondary parenthetical. We count
  // UNIQUE staff across all groups so an assistant covering two shoots
  // isn't double-counted; falls back to the flat assignment total for
  // rows without a staff_id (freelancers etc.).
  const shootCount = groups.length;
  const uniqueCrew = new Set();
  let assignmentsWithoutStaff = 0;
  groups.forEach((g) => {
    (g.assignments || []).forEach((a) => {
      if (a.staff_id) uniqueCrew.add(a.staff_id);
      else assignmentsWithoutStaff += 1;
    });
  });
  const crewCount = uniqueCrew.size + assignmentsWithoutStaff;

  return (
    <div className="card overflow-hidden" data-testid="today-on-set-widget">
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 bg-gradient-to-r from-rose-50/60 to-white">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-800 shrink-0">
            <Video className="w-4 h-4" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-base font-semibold truncate">Today on set</h2>
            <div className="text-[11px] text-stone-500">{dateLabel}</div>
          </div>
          {shootCount > 0 && (
            <span className="badge badge-rose ml-2" data-testid="today-on-set-count">
              {shootCount} {shootCount === 1 ? "shoot" : "shoots"} today
              {crewCount > 0 && (
                <span className="opacity-80 ml-1">
                  ({crewCount} {crewCount === 1 ? "crew member" : "crew members"})
                </span>
              )}
            </span>
          )}
        </div>
        <Link to="/team" className="text-xs text-rose-800 font-semibold hover:underline inline-flex items-center gap-1">
          Open crew board <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
        </Link>
      </div>

      {groups.length === 0 ? (
        <div className="p-6 text-center text-sm text-stone-500" data-testid="today-on-set-empty">
          Nothing scheduled for today.{" "}
          <Link to="/team" className="text-rose-700 font-semibold hover:underline">Assign a crew →</Link>
        </div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {groups.map((g) => (
            <li key={`${g.client_name}||${g.place}`} className="px-5 py-3" data-testid={`today-on-set-group-${g.client_name}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-stone-900 truncate">🎬 {g.client_name || "—"}</div>
                  {g.place && (
                    <div className="inline-flex items-center gap-1 text-xs text-stone-500">
                      <MapPin className="w-3 h-3" strokeWidth={2} /> {g.place}
                    </div>
                  )}
                </div>
                <span className="badge badge-neutral text-[10px]">
                  {g.assignments.length} {g.assignments.length === 1 ? "role" : "roles"}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {g.assignments.map((a) => (
                  <div key={a.id} className="text-xs bg-stone-50 border border-stone-100 rounded-md px-2.5 py-1.5" data-testid={`today-on-set-assignment-${a.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-stone-800 truncate">
                        {a.job_role || "CREW"}
                        <span className="text-stone-400 mx-1">·</span>
                        <span className="text-stone-700">{a.staff_name || "—"}</span>
                      </span>
                      {a.event && <span className="text-[10px] text-stone-500 uppercase tracking-wider shrink-0">{a.event}</span>}
                    </div>
                    {a.equipment.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" title={a.equipment.join(" + ")}>
                        <Camera className="w-3 h-3 text-stone-400 shrink-0 mt-0.5" strokeWidth={2} />
                        {a.equipment.map((e) => (
                          <span key={e} className="text-[10px] bg-white border border-stone-200 rounded-full px-1.5 py-0.5 text-stone-600">{e}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function reminderMessage(item) {
  const couple = item.couple_name || "there";
  const ev = item.event ? ` for your ${item.event}` : "";
  const date = item.event_date ? ` (held on ${fmtDate(item.event_date)})` : "";
  const amt = inr(item.pending_amount);
  return `Hi ${couple}, gentle reminder — a balance of ${amt}${ev}${date} is currently pending. Please let us know a convenient time to settle this. Thank you! — Kahani by Rakesh Janardan`;
}

function severityClass(days) {
  if (days === null || days === undefined) return "text-stone-500";
  if (days >= 60) return "text-red-700 font-semibold";
  if (days >= 30) return "text-amber-700 font-semibold";
  if (days > 0) return "text-stone-700";
  return "text-stone-400";
}

function severityLabel(days) {
  if (days === null || days === undefined) return "—";
  if (days < 0) return `in ${-days}d`;
  if (days === 0) return "today";
  return `${days}d overdue`;
}

// iter170 — Weekly backup reminder banner. Polls `/backup/reminder/status`
// once on mount and quietly hides itself if the admin already downloaded a
// verified backup this week (Mon 00:00 → Sun 23:59 UTC).
function BackupReminderBanner() {
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/backup/reminder/status")
      .then(({ data }) => { if (alive) setState(data); })
      .catch(() => { if (alive) setState(null); });
    return () => { alive = false; };
  }, []);
  if (!state || !state.needs_backup) return null;
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
      data-testid="dashboard-backup-reminder"
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold flex items-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.99-1.743 2.99H3.482c-1.53 0-2.493-1.656-1.743-2.99L8.257 3.1zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-7a1 1 0 00-1 1v3a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Backup Reminder
        </div>
        <p className="text-xs mt-0.5">
          You haven't downloaded a full backup this week (Mon–Sun UTC). Keep your Kahani Studio recovery copy up to date.
        </p>
      </div>
      <Link to="/backup" className="btn btn-primary btn-sm shrink-0" data-testid="dashboard-backup-reminder-cta">
        Download Full Backup
      </Link>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";   // iter137 — hide bookings surfaces
  const [stats, setStats] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [today, setToday] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => logger.debug("dashboard stats failed:", e?.message));
    // iter127 — /dashboard/overdue is admin-only (403 for others).
    // Skip the fetch entirely so we don't spam the console with 403s.
    if (isAdmin) {
      api.get("/dashboard/overdue")
        .then((r) => setOverdue(r.data))
        .catch((e) => logger.debug("overdue load failed:", e?.message));
    }
    api.get("/team/crew/today")
      .then((r) => setToday(r.data))
      .catch((e) => logger.debug("today-on-set load failed:", e?.message));
  }, [isAdmin]);

  if (!stats) return <div className="text-stone-500">Loading…</div>;

  const overdueTotal = overdue.reduce((acc, x) => acc + (x.pending_amount || 0), 0);
  const overdue30plus = overdue.filter((x) => (x.days_overdue ?? -1) >= 30);
  // iter127 — Financial blocks (revenue metric cards, monthly financials,
  // overdue widget) are admin-only. Managers see the Command Center +
  // ops widgets only. `stats.revenue` is undefined when the API strips
  // the financial block for non-admin callers.
  const hasRevenue = Boolean(stats.revenue);

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard-page">
      <PageHero
        eyebrow="Kahani Studio · Overview"
        title="Your studio at a glance"
        subtitle="Bookings, post-production, cash flow and today's set — one editorial dashboard, refreshed live."
      />

      {/* iter170 — Weekly backup reminder (admin only) */}
      {user?.role === "admin" && <BackupReminderBanner />}

      {/* Iter127 — Today's Command Center (always visible at top) */}
      <CommandCenter />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        {/* iter137 — Bookings metric is hidden for Manager (no Bookings access). */}
        {!isManager && (
          <MetricCard
            testid="metric-total-bookings"
            icon={ClipboardList}
            eyebrow="Total Bookings"
            value={stats.bookings.total}
            subtitle={`${stats.bookings.confirmed} confirmed · ${stats.bookings.waiting} waiting`}
            tone="wine"
          />
        )}
        <MetricCard
          testid="metric-postproduction"
          icon={ScissorsLineDashed}
          eyebrow="Post-Production"
          value={stats.post_production.total}
          subtitle={`${stats.post_production.done} done · ${stats.post_production.pending} pending`}
          tone="amber"
        />
        {hasRevenue && (
          <>
            <MetricCard
              testid="metric-revenue"
              icon={IndianRupee}
              eyebrow="Total Billed"
              value={inr(stats.revenue.total)}
              subtitle={`Paid ${inr(stats.revenue.paid)}`}
              tone="emerald"
            />
            <MetricCard
              testid="metric-pending"
              icon={TrendingUp}
              eyebrow="Payment Pending"
              value={inr(stats.revenue.pending)}
              subtitle="Outstanding across all events"
              tone="red"
            />
          </>
        )}
      </div>

      {/* Today on set — quick admin snapshot from the Shoot Crew board */}
      <TodayOnSet data={today} />

      {/* iter192 — P&L overview (admin-only): donut + shoot profitability. */}
      {isAdmin && <ProfitLossOverview />}

      {/* iter51 — Monthly Financial Dashboard (admin-only) */}
      {isAdmin && <MonthlyFinancials />}

      {/* Overdue Payments Widget — admin-only, financial */}
      {isAdmin && (
      <div className="card overflow-hidden" data-testid="overdue-widget">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 bg-gradient-to-r from-rose-50/50 to-amber-50/30">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-700" strokeWidth={1.7} />
            <h2 className="font-heading text-base font-semibold">Overdue payments</h2>
            {overdue30plus.length > 0 && (
              <span className="badge badge-red ml-1">{overdue30plus.length} · 30d+</span>
            )}
          </div>
          <div className="text-xs text-stone-600">
            Outstanding: <span className="font-semibold text-rose-800 tabular-nums">{inr(overdueTotal)}</span>
          </div>
        </div>
        {overdue.length === 0 ? (
          <div className="p-10 text-center text-stone-500 text-sm">
            All clear — no pending payments right now. ✨
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Event Date</th>
                  <th>Couple</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th className="text-right">Pending</th>
                  <th>Overdue</th>
                  <th className="text-right">Reminder</th>
                </tr>
              </thead>
              <tbody>
                {overdue.slice(0, 10).map((o) => (
                  <tr key={o.id} data-testid={`overdue-row-${o.id}`}>
                    <td className="font-mono text-xs">{fmtDate(o.event_date)}</td>
                    <td className="font-medium">{o.couple_name}</td>
                    <td>{o.event}</td>
                    <td><span className={statusBadgeClass(o.status)}>{o.status}</span></td>
                    <td className="text-right font-mono tabular-nums text-red-700 font-semibold">{inr(o.pending_amount)}</td>
                    <td className={`font-mono text-xs ${severityClass(o.days_overdue)}`}>{severityLabel(o.days_overdue)}</td>
                    <td className="text-right whitespace-nowrap">
                      {o.contact ? (
                        <a
                          href={waLink(o.contact, reminderMessage(o))}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 text-xs font-medium transition-colors"
                          data-testid={`overdue-wa-${o.id}`}
                          title="Send WhatsApp reminder"
                        >
                          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.8} /> WhatsApp
                        </a>
                      ) : (
                        <span className="text-[11px] text-stone-400 italic" data-testid={`overdue-nocontact-${o.id}`} title="Add a contact to enable WhatsApp reminders">
                          no contact
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {overdue.length > 10 && (
              <div className="px-5 py-3 text-xs text-stone-500 border-t border-stone-100">
                Showing 10 of {overdue.length} entries. <Link to="/post-production" className="text-rose-700 hover:underline">View all in Post-Production →</Link>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* iter137 — Upcoming events widget removed for Manager (no Bookings surface). */}
      {!isManager && (
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-stone-500" strokeWidth={1.6} />
            <h2 className="font-heading text-base font-semibold">Upcoming events (next 60 days)</h2>
          </div>
          <Link to="/bookings" className="text-xs text-rose-700 hover:underline flex items-center gap-1" data-testid="link-view-all-bookings">
            View all <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        {stats.upcoming_events.length === 0 ? (
          <div className="p-10 text-center text-stone-500 text-sm">No upcoming events. Add a booking to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Client</th>
                  <th>Couple</th>
                  <th>Event</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {stats.upcoming_events.map((b) => (
                  <tr key={b.id} data-testid={`upcoming-row-${b.id}`}>
                    <td className="font-mono text-xs">{fmtDate(b.day1)}</td>
                    <td><span className={statusBadgeClass(b.status)}>{b.status}</span></td>
                    <td className="font-medium">{b.client_name}</td>
                    <td>{b.couple_name || "—"}</td>
                    <td>{b.event || "—"}</td>
                    <td className="text-stone-600">{b.location || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
